import z from 'zod';
import { getAddressSchema } from '../address';
import { Draft } from 'src/types/common';
import { IOrgModel } from './orgInterface';
import { TYPE_CODE_K12, TYPE_CODE_OTHER_ED } from './constants';
import { EmailSchema, UrlSchema } from '../validation';

const ERROR_ONLY_NUMBERS = "Must contain only numeric values";
const Phone = z.object({
  formatted: z.string().optional(),
  phoneCc: z.string().min(1).max(3).regex(new RegExp(/^\d{1,3}$/), ERROR_ONLY_NUMBERS).optional(),
  phoneNdc: z.string().min(2).max(5).regex(new RegExp(/^\d{2,5}$/), ERROR_ONLY_NUMBERS).optional(),
  phoneLocal: z.string().min(4).max(10).regex(new RegExp(/^\d{4,10}$/), ERROR_ONLY_NUMBERS).optional()
});

const FeeWaiverSchema = z.string().regex(new RegExp(/^\d{2}[ABTN]\d{3}$/), 'Fee Waiver Id must follow proper format: ##X### where # represents a numeric digit and X represents the letter A,B,T or N.');
const NcesSchema = z.string().regex(/^([\d|\w]{7}|[\d|\w]{8}|[\d|\w]{12})$/, 'must have 7 (LEAID), 8 (PPIN), or 12 (NCESSCH) alpha numeric characters');
export const NoForeignValues = (zodVal: z.ZodString) => zodVal.regex(/^[\x00-\x7F]*$/, "String contains foreign characters or numbers");

const NonEmptyString = z.string().min(1);
const Org = z.object({
  orgId: NonEmptyString.optional(),
//   name: NonEmptyString.max(255),
  nameShort: NonEmptyString.max(50),
  nameAbbr: NonEmptyString.max(30),
  nameShortAbbr: z.string().max(20).optional(),
  nameNick: z.string().max(255).optional(),
  nameFormerOfficial: z.string().max(255).optional(),
  nameRoot: z.string().max(60).optional(),
  website: UrlSchema.optional(),
  email: EmailSchema.optional(),
  orgTypeCd: z.number(),
  orgSubTypeCd: z.number(),
  phone: Phone.optional(),
  fax: Phone.optional(),
  mdrPid: NonEmptyString.max(8).optional(),
  ncesId: NcesSchema.optional(),
  ipedsId: NonEmptyString.max(6).optional(),
  feeWaiverId: FeeWaiverSchema.optional(),
  orgCodeNotes: NonEmptyString.max(1000).optional(),
});

/*
 * Per new requirements, now lowest and highest grade options need to be displayed for all org types
 * and we need to make Highest/Lowest Grade Level optional for all OrgTypeCd for operations form
 * But getGradeSchema logic is being used for other fields like school sub type cd, school level cd
 */
const getGradeSchema = (orgTypeCd: number | undefined) => {
    const number = z.number();
    if (orgTypeCd === TYPE_CODE_K12 || orgTypeCd === TYPE_CODE_OTHER_ED) {
        return number.optional();
    } else {
        return z.undefined();
    }
};

/**
 * Adding new validation check to comply with this database trigger: K12.HIGHEST_GRADE_LVL_CD/LOWEST_GRADE_LVL_CD
 * If lowest grade is selected from dropdown,the highest grade also need to be selected from its dropdown and vice versa
 * If lowest grade is not selected, then highest need not be selected and vice versa
 *
 * @param highestGradeCd
 * @param lowestGradeCd
 */
const getHighestLowestGradesSchema = (highestGradeCd: number | undefined, lowestGradeCd: number | undefined) => {

    if ((highestGradeCd && !lowestGradeCd) || (!highestGradeCd && lowestGradeCd)) {
        return z.number();
    } else if ((!highestGradeCd && !lowestGradeCd) || (highestGradeCd && lowestGradeCd)) {
        return z.number().optional();
    } else {
        return z.undefined();
    }

}

const charterInd = z.boolean();
const campus = z.boolean();

export const getSchema = (update: Draft<IOrgModel>, preparing: boolean,type:string) => {
    const {visitingAddress, shippingAddress} = update;
    const VisitingAddressSchema = getAddressSchema(visitingAddress?.countryIsoCd);
    const ShippingAddressSchema = getAddressSchema(shippingAddress?.countryIsoCd);
    
    // In RDS production, currently there are hundreds of orgs with education delivery method codes in (0,1,2,3,4,5,6)
    const deliveryMethodSchema = z.array(z.coerce
        .string()
        .regex(/^[0|1|2|3|4|5|6]$/, "Must be a valid delivery method") 
        .transform((code) => parseInt(code)));
    return Org.extend({
        name: NoForeignValues(NonEmptyString.max(255)),
        visitingAddress: VisitingAddressSchema,
        shippingAddress: ShippingAddressSchema.optional(),
        highestGradeCd: getHighestLowestGradesSchema(update.highestGradeCd, update.lowestGradeCd),
        lowestGradeCd: getHighestLowestGradesSchema(update.highestGradeCd, update.lowestGradeCd),
        charterInd: update.orgTypeCd === TYPE_CODE_K12 ? charterInd : charterInd.optional(),
        campus: update.orgTypeCd === TYPE_CODE_K12 ? campus : campus.optional(),
        schoolSubTypeCd: z.number().optional(),
        schoolLevelCd: z.number().optional(),
        educationDeliveryMethodCds: update.orgTypeCd === TYPE_CODE_K12 || update.orgTypeCd === TYPE_CODE_OTHER_ED ? deliveryMethodSchema.min(1, 'Must select at least 1 education delivery method') : deliveryMethodSchema.optional(),
        instLvlCd: type === 'di' ? z.literal(-3).or(z.literal(1)).or(z.literal(2)).or(z.literal(3)) : z.literal(-3).optional().or(z.literal(1).optional()).or(z.literal(2).optional()).or(z.literal(3).optional())
    });
};
